import { Fragment, type ReactNode } from 'react'

import { faLocationDot } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import {
  formatAddressShort,
  getNumberOfUnitsInLot,
} from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedListingDetails,
  inventoryGroupsQuery,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage } from '@b-stock/fe-core/bstock-react'
import {
  Breakpoints,
  Colors,
  Typography,
  pxToRem,
} from '@b-stock/fe-core/design-system'
import { CommonStrings, ListingEnums } from '@b-stock/fe-core/locale'
import { designColors } from '@b-stock/fe-core/theme'

import { getTopLotCategory } from '@/buyer-portal/helpers/lot/categories'

import { calculateShipmentDetails } from '../../Shipment/utils'

const Row = styled.div`
  ${Typography.Body2}
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1rem;

  @media ${Breakpoints.max.small} {
    ${Typography.Body4}
    gap: 0.25rem;
  }
`

const Segment = styled.span`
  align-items: center;
  color: ${designColors.neutral.veryDarkGray};
  display: flex;
  flex-grow: 0;
  flex-shrink: 0;
  gap: 0 ${pxToRem(6)};
  max-width: 100%;
`

const Divider = styled.span`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  justify-content: center;

  &::after {
    background-color: ${Colors.Semantic.Neutral.medium_grey};
    content: '';
    display: block;
    height: ${pxToRem(24)};
    width: 1px;
  }

  @media ${Breakpoints.max.small} {
    &::after {
      height: ${pxToRem(18)};
    }
  }
`

const LocationBold = styled.span`
  ${Typography.Subtitle1}

  @media ${Breakpoints.max.small} {
    ${Typography.Subtitle2}
    gap: 0.25rem;
  }
`

const PinIcon = styled(FontAwesomeIcon)`
  color: ${designColors.error.highlight};
  flex-shrink: 0;
`

export const ListingKeyAttributes = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const location = listing.lot.location
  const shortAddress = formatAddressShort(location, true)
  const prettyId = 'prettyId' in location ? location.prettyId : null

  const unitCount = getNumberOfUnitsInLot(listing.lot)

  // Pallet spaces are only known once the groups load, so the segment renders
  // as the bare transport mode first and gains the count when it arrives.
  const groups = inventoryGroupsQuery.useQuery(listing.lot._id).data?.groups
  const { totalPalletSpaces, showPalletSpaces } = calculateShipmentDetails(
    groups ?? []
  )
  // A PALLET group with no recorded dimensions reports showPalletSpaces with a
  // total of 0. ListingDetailsCard renders that as a labelled "0", but a bare
  // "0 Pallets" in the header reads as a real count, so drop the segment.
  const palletSpaces =
    showPalletSpaces && totalPalletSpaces ? totalPalletSpaces : null

  const transportMode = listing.shipping?.transportMode
  const hasShipping = !!transportMode || palletSpaces != null

  const topCategory = getTopLotCategory(listing.lot)

  const segments: { key: string; content: ReactNode }[] = []

  segments.push({
    key: 'location',
    content: (
      <Segment>
        <PinIcon icon={faLocationDot} size="sm" />
        <LocationBold>{shortAddress}</LocationBold>
      </Segment>
    ),
  })

  if (prettyId) {
    segments.push({
      key: 'facility',
      content: (
        <Segment>
          <FormattedMessage
            id="Listing.details.facility"
            values={{ prettyId }}
          />
        </Segment>
      ),
    })
  }

  if (unitCount) {
    segments.push({
      key: 'units',
      content: (
        <Segment>
          <FormattedMessage
            id="Listing.details.units"
            values={{ count: unitCount }}
          />
        </Segment>
      ),
    })
  }

  segments.push({
    key: 'category',
    content: (
      <Segment data-testid="category-segment">
        {topCategory ? (
          <ListingEnums.Subcategory.Component value={topCategory} />
        ) : (
          <CommonStrings.Unspecified.Component />
        )}
      </Segment>
    ),
  })

  if (hasShipping) {
    segments.push({
      key: 'shipping',
      content: (
        <Segment data-testid="shipping-segment">
          {transportMode}
          {transportMode && palletSpaces != null && ' · '}
          {palletSpaces != null && (
            <FormattedMessage
              id="Listing.details.palletsCount"
              values={{ count: palletSpaces }}
            />
          )}
        </Segment>
      ),
    })
  }

  return (
    <Row>
      {segments.map(({ key, content }, i) => (
        <Fragment key={key}>
          {i > 0 && <Divider aria-hidden="true" />}
          {content}
        </Fragment>
      ))}
    </Row>
  )
}
